import { supervisedAssuranceAuthenticatorVerify } from '@api/selfServiceServices';
import ContinueButton from '@components/continueButton/ContinueButton';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { InputBox } from '@imprivata-cloud/components';
import { generatePKCECodes, type PKCECodes } from '@selfservice/utils/pkce';
import { Form } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EpcsSetupStep } from '../EpcsSetup';

enum FieldNames {
  TOKEN_CODE = 'tokenCode',
}

interface FormValues {
  [FieldNames.TOKEN_CODE]: string;
}

const TRANSLATION = 'self-service.epcs-setup.verify-iid';
const TOKEN_CODE_LENGTH = 6;

interface VerifyAuthenticatorProps {
  assuranceId: string;
  authenticatorId: string;
  authenticatorShortName: string;
  authenticatorDeviceModel?: string;
  tenantId?: string;
  workflowId: string;
  setPKCECodes: (codes: PKCECodes) => void;
  setStep: (step: EpcsSetupStep) => void;
  cancelFlow: () => void;
}

const EpcsStep3VerifyAuthenticator = ({
  assuranceId,
  authenticatorId,
  authenticatorShortName,
  authenticatorDeviceModel,
  tenantId,
  workflowId,
  setPKCECodes,
  setStep,
  cancelFlow,
}: VerifyAuthenticatorProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const { emitError } = useErrorContext();
  const tokenCode = Form.useWatch(FieldNames.TOKEN_CODE, form);
  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await supervisedAssuranceAuthenticatorVerify(
          assuranceId,
          authenticatorId,
          values[FieldNames.TOKEN_CODE],
          workflowId,
          tenantId,
        );

        const pkceCodes = await generatePKCECodes();
        setPKCECodes(pkceCodes);
        setStep(EpcsSetupStep.STEP4_SUPERVISOR_AUTHENTICATE);
      } catch (error) {
        emitError(new AppError(error as ApiError));
      }
    },
    [emitError, assuranceId, authenticatorId, tenantId, workflowId, setPKCECodes, setStep],
  );

  return (
    <Form<FormValues> className="continue-layout" form={form} onFinish={handleSubmit}>
      <div>
        <h2 className="title">
          {t(`${TRANSLATION}.title`, { deviceName: authenticatorDeviceModel || authenticatorShortName })}
        </h2>
        <p>{t(`${TRANSLATION}.instructions`)}</p>
        <Form.Item name={FieldNames.TOKEN_CODE}>
          <InputBox
            label={t(`${TRANSLATION}.token-code`)}
            required
            type="text"
            aria-label="token-code"
            maxLength={TOKEN_CODE_LENGTH}
            inputMode="numeric"
          />
        </Form.Item>
      </div>

      <div>
        <ContinueButton
          htmlType="submit"
          disabled={tokenCode?.length !== TOKEN_CODE_LENGTH}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

export default EpcsStep3VerifyAuthenticator;
